import React, { useState } from "react";
import { orderStatuses } from "@/utils/orderUtils";
import { PageContainer } from "@/components/shared/page-container";
import { HeaderSection } from "@/components/shared/header-section";
import { useQuery } from "@apollo/client";
import { GET_ORDERS } from "@/containers/Orders/OrderQueries";
import { DataTableManual } from "@/components/shared/data-table-manual/data-table-manual";
import { columns } from "@/containers/Orders/components/columns";
import { DataTableToolbar } from "@/components/shared/data-table-manual/data-table-toolbar-manual";
import { SortingState } from "@tanstack/react-table";
import { useParams } from "react-router-dom";

export default function CustomerOrders() {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<Map<string, Set<string>>>(new Map());
  const { accountId } = useParams();

  // Prepare query variables
  const queryVariables = React.useMemo(() => {
    const filterArray: any = Array.from(filters.entries())
      .map(([key, value]) => {
        if (!value.size) return null;
        return {
          [key]: {
            in: Array.from(value),
          },
        };
      })
      .filter(Boolean);

    filterArray.push({
      purchased_by: {
        is: {
          id: {
            equals: Number(accountId),
          },
        },
      },
    });

    return {
      take: pageSize,
      skip: pageIndex * pageSize,
      searchTerm: searchTerm || undefined,
      searchTermInteger: searchTerm ? parseInt(searchTerm) : undefined,
      orderBy: sorting.map((sort) => ({
        [sort.id]: sort.desc ? "desc" : "asc",
      })),
      filters: filterArray,
    };
  }, [pageSize, pageIndex, searchTerm, sorting, filters]);

  const { data, loading } = useQuery(GET_ORDERS, {
    variables: queryVariables,
    fetchPolicy: "network-only", // Always fetch from network when variables change
  });

  // Reset pagination when filters or search changes
  React.useEffect(() => {
    setPageIndex(0);
  }, [searchTerm, filters]);

  const handlePaginationChange = (updater: any) => {
    const newPagination = updater({
      pageIndex,
      pageSize,
    });
    setPageIndex(newPagination.pageIndex);
    setPageSize(newPagination.pageSize);
  };

  const toolbarConfig = {
    search: {
      onSearch: (search: string) => setSearchTerm(search),
      onFilter: (value: any) => setFilters(value),
      placeholder: "Search orders...",
      accessor: "searchTerm",
    },
    columns: [
      {
        title: "Status",
        accessor: "order_status",
        options: Object.entries(orderStatuses).map(([value, label]) => ({
          label,
          value,
        })),
      },
    ],
  };

  return (
    <PageContainer>
      <HeaderSection title="Orders" subtitle="Manage and view all orders" />
      <div className="mt-8">
        <DataTableManual
          data={data?.findManyOrder || []}
          columns={columns}
          pageIndex={pageIndex}
          pageSize={pageSize}
          rowCount={data?.findManyOrderCount || 0}
          onPaginationChange={handlePaginationChange}
          onSortingChange={setSorting}
          onColumnFiltersChange={setFilters}
          toolbar={(table) => (
            <DataTableToolbar table={table} config={toolbarConfig} />
          )}
        />
      </div>
    </PageContainer>
  );
}
